import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { loadStripe } from '@stripe/stripe-js';
import { environment as env } from '@env/environment';
import {
  AppFormService,
  IPaymentIntentRequest,
  IPaymentIntentResponse,
  IPaymentPortalDetails,
  PlatformService,
  TitleService,
  TooltipDirective,
} from '@sml/utility';
import { SpinnerService } from '@sml/spinner';
import { ToastrService } from '@sml/toastr';
import { IconsComponent } from "@sml/icons";

@Component({
  selector: 'lib-application-payment',
  standalone: true,
  imports: [CommonModule, FormsModule, IconsComponent, TooltipDirective],
  templateUrl: './application-payment.component.html',
  styleUrl: './application-payment.component.scss',
})
export class ApplicationPaymentComponent implements OnInit {
  applicationId = '';
  details: IPaymentPortalDetails | null = null;
  isLoadingDetails = true;
  loadError: string | null = null;
  stripe: any;
  elements: any;
  card: any;
  bankAccount: any;
  paymentRequest: any;
  googlePayButton: any;
  paymentMethodHandler: any;
  isGooglePayAvailable = false;
  isPaymentIntentLoaded = false;
  isShowPaymentLoader = false;
  isPaymentComplete = false;
  selectedPaymentMethod: string | null = null;
  paymentIntentResponse!: IPaymentIntentResponse;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly appFormService: AppFormService,
    private readonly spinnerService: SpinnerService,
    private readonly toaster: ToastrService,
    private readonly cdr: ChangeDetectorRef,
    private readonly platformService: PlatformService,
    private readonly titleService: TitleService,
  ) {}

  ngOnInit(): void {
    this.titleService.updateTitle('SeeMyLegacy - Payment Page');
    this.applicationId = this.route.snapshot.paramMap.get('applicationId') ?? '';
    if (!this.applicationId) {
      this.loadError = 'Invalid payment link.';
      this.isLoadingDetails = false;
      return;
    }
    this.loadDetailsAndStripe();
  }

  private async loadDetailsAndStripe(): Promise<void> {
    if (!this.platformService.browser()) {
      return;
    }

    try {
      const detailsPromise: Promise<IPaymentPortalDetails | null> =
        firstValueFrom(this.appFormService.getPaymentPortalDetails(this.applicationId))
          .then((portal) => {
            if (!portal) return null;
            if (portal.canPay === false) {
              throw new Error('This application is not eligible for payment at this time.');
            }
            return portal;
          });

      const [details, stripe] = await Promise.all([
        detailsPromise,
        loadStripe(env.stripePublishableKey),
        new Promise((resolve) => setTimeout(resolve, 2000)),
      ]);

      if (!details) {
        this.loadError = 'Payment details could not be loaded.';
        this.isLoadingDetails = false;
        return;
      }

      this.details = details;
      this.stripe = stripe;
      this.isLoadingDetails = false;

      this.paymentRequest = this.stripe.paymentRequest({
        country: 'US',
        currency: 'usd',
        total: {
          label: 'Total',
          amount: Math.round((details.approvedPaymentAmount ?? 0) * 100),
        },
      });

      const canPayResult = await this.paymentRequest.canMakePayment();
      this.isGooglePayAvailable = !!canPayResult?.googlePay;
      // this.isGooglePayAvailable = true;
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error loading application payment details:', error);
      this.loadError = error instanceof Error
        ? error.message
        : 'Unable to load payment details. Please try again later.';
      this.isLoadingDetails = false;
      this.cdr.detectChanges();
    }
  }

  onPaymentMethodChange(method: string): void {
    if (!this.stripe || !this.details) {
      return;
    }
    this.isPaymentIntentLoaded = false;

    const paymentIntentRequest: IPaymentIntentRequest = {
      amount: Math.round((this.details.approvedPaymentAmount ?? 0) * 100),
      email: this.details.email ?? '',
      name: `${this.details.firstName} ${this.details.lastName}`.trim(),
      phone: this.details.phone ?? '',
      postalCode: this.details.postalCode ?? '',
      paymentMethod: method === 'gpay' ? 'card' : method,
      metaData: {
        ApplicationId: this.applicationId,
        ItemId: this.details.itemId ? String(this.details.itemId) : '',
        PaymentMethod: method,
      },
      descriptor: this.details.descriptor ?? undefined,
      donorId: this.details.activityLogId
    };

    this.appFormService.getPaymentIntent(paymentIntentRequest).subscribe({
      next: (response) => {
        if (!response?.clientSecret) {
          this.toaster.error({ message: 'Failed to initialize payment.', title: 'Payment Error' });
          return;
        }
        this.paymentIntentResponse = response;
        this.elements = this.stripe.elements({ clientSecret: response.clientSecret });

        if (method === 'gpay') {
          this.bindGooglePayHandler();
        }

        this.googlePayButton = this.elements.create('paymentRequestButton', {
          paymentRequest: this.paymentRequest,
        });
        this.card = this.elements.create('card', {
          hidePostalCode: false,
          style: {
            base: {
              color: '#041519',
              fontFamily: 'Satoshi, system-ui, -apple-system, sans-serif',
              fontSize: '15px',
              fontWeight: '500',
              fontSmoothing: 'antialiased',
              iconColor: '#106B8E',
              '::placeholder': { color: '#94A3AE' },
            },
            invalid: {
              color: '#C0392B',
              iconColor: '#C0392B',
            },
          },
        });
        this.bankAccount = this.elements.create('payment', {
          fields: {
            billingDetails: {
              name: 'never',
              email: 'never',
            },
          },
          paymentMethodOrder: ['us_bank_account'],
        });

        this.mountElement(method);
      },
      error: (error) => {
        console.error('Payment intent error:', error);
        this.toaster.error({ message: 'Failed to initialize payment.', title: 'Payment Error' });
      },
    });
  }

  private bindGooglePayHandler(): void {
    if (this.paymentMethodHandler) {
      this.paymentRequest.off('paymentmethod', this.paymentMethodHandler);
    }
    this.paymentMethodHandler = async (ev: any) => {
      const clientSecret = this.paymentIntentResponse.clientSecret;
      try {
        const { error, paymentIntent } = await this.stripe.confirmCardPayment(
          clientSecret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false },
        );
        if (error) {
          ev.complete('fail');
          this.toaster.error({ message: error?.message ?? 'Unknown Error', title: 'Payment Failed' });
          return;
        }
        if (paymentIntent.status === 'succeeded') {
          ev.complete('success');
          this.handlePaymentSuccess();
        } else if (paymentIntent.status === 'requires_action') {
          const retry = await this.stripe.confirmCardPayment(clientSecret);
          if (retry.error || retry.paymentIntent?.status !== 'succeeded') {
            ev.complete('fail');
            this.toaster.error({
              message: retry.error?.message ?? 'Payment failed.',
              title: 'Payment Failed',
            });
            return;
          }
          ev.complete('success');
          this.handlePaymentSuccess();
        } else {
          ev.complete('fail');
          this.toaster.error({ message: paymentIntent.status, title: 'Payment Failed' });
        }
      } catch (e) {
        ev.complete('fail');
        this.toaster.error({ message: 'Unknown Error', title: 'Payment Failed' });
      }
    };
    this.paymentRequest.on('paymentmethod', this.paymentMethodHandler);
  }

  private mountElement(method: string): void {
    if (this.card?.isMounted) this.card.unmount();
    if (this.bankAccount?.isMounted) this.bankAccount.unmount();
    if (this.googlePayButton?.isMounted) this.googlePayButton.unmount();

    setTimeout(() => {
      if (method === 'card') {
        this.card.mount('#ap-card-element');
        this.isPaymentIntentLoaded = true;
      } else if (method === 'bank') {
        this.bankAccount.mount('#ap-bank-account-element');
        this.isPaymentIntentLoaded = true;
      } else if (method === 'gpay') {
        this.paymentRequest.canMakePayment().then((result: any) => {
          if (result) {
            this.googlePayButton.mount('#ap-google-pay-button');
            this.isPaymentIntentLoaded = true;
          } else {
            this.toaster.error({ message: 'Google Pay is not available.', title: 'Unavailable' });
          }
        });
      }
      this.cdr.detectChanges();
    }, 0);
  }

  async handleSubmit(event: Event) {
    if (this.isPaymentIntentLoaded) {
      this.isShowPaymentLoader = true;
      this.spinnerService.showSpinner();
    }
    if (!this.isPaymentIntentLoaded) {
      this.spinnerService.hideSpinner();
      this.toaster.error({ message: 'Payment initialization failed. Please try again.', title: 'Payment Failed' });
      console.error('Payment initialization failed: Missing clientSecret');
      return;
    }

    const { error, paymentIntent } = this.selectedPaymentMethod === 'card'
      ? await this.confirmCardPayment(this.paymentIntentResponse.clientSecret)
      : this.selectedPaymentMethod === 'bank' ? await this.confirmPayment(this.paymentIntentResponse.clientSecret)
        : { error: { message: 'Unsupported payment method' }, paymentIntent: null as any };

    if (!this.selectedPaymentMethod) {
      this.isShowPaymentLoader = false;
      this.spinnerService.hideSpinner();
      this.toaster.error({ message: `Please select a Payment Method for your submission.`, title: 'Payment method not selected' });
      return;
    }
    else if (error) {
      this.isShowPaymentLoader = false;
      this.spinnerService.hideSpinner();
      this.toaster.error({ message: error?.message ?? 'Unknown Error', title: 'Payment Failed' });
      console.error('Payment error:', error);
    }
    else if (
      (this.selectedPaymentMethod === 'bank' &&
        (paymentIntent.status === 'processing' || paymentIntent.status === 'requires_action')) ||
      (this.selectedPaymentMethod === 'card' && paymentIntent.status === 'succeeded')
    ) {
      this.isShowPaymentLoader = false;
      this.handlePaymentSuccess();
      this.spinnerService.hideSpinner();
    }
    else {
      this.spinnerService.hideSpinner();
      const failedObj = {
        message: 'Payment failed and requires additional action.',
        paymentStatus: paymentIntent?.status,
        paymentMethod: this.selectedPaymentMethod,
      };
      this.toaster.error({ message: JSON.stringify(failedObj), title: 'Payment Failed' });
      console.error('Payment failed:', failedObj);
    }
  }

  private async confirmCardPayment(clientSecret: string) {
    return await this.stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: this.card,
        billing_details: {
          name: `${this.details?.firstName ?? ''} ${this.details?.lastName ?? ''}`.trim(),
          email: this.details?.email ?? '',
          phone: this.details?.phone ?? '',
          address: {
            postal_code: this.details?.postalCode ?? '',
          },
        },
      },
    });
  }

  private async confirmPayment(clientSecret: string) {
    return await this.stripe.confirmPayment({
      elements: this.elements,
      confirmParams: {
        payment_method_data: {
          billing_details: {
            name: `${this.details?.firstName ?? ''} ${this.details?.lastName ?? ''}`.trim(),
            email: this.details?.email ?? '',
            phone: this.details?.phone ?? '',
            address: {
              postal_code: this.details?.postalCode ?? '',
            },
          },
        },
      },
      redirect: 'if_required',
    });
  }

  private handlePaymentSuccess(): void {
    this.isPaymentComplete = true;
    this.cdr.detectChanges();
    this.appFormService.getPaymentPortalDetails(this.applicationId).subscribe({
      next: () => {/* no-op: success screen is the source of truth for this view */},
      error: () => {/* no-op: refresh will re-evaluate via ngOnInit */},
    });
  }
}
